import { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useTranslation } from "react-i18next";
import { User as UserIcon } from "lucide-react";

interface AppLayoutProps {
  children: ReactNode;
  activeView?: 'chat' | 'methods' | 'loop';
  onViewChange?: (view: 'chat' | 'methods' | 'loop') => void;
  profile?: any;
  // Conversation props (optional)
  conversations?: any[];
  currentConversationId?: string | null;
  onSelectConversation?: (id: string) => void;
  onNewConversation?: () => Promise<void>;
  onDeleteConversation?: (id: string) => Promise<void>;
  onRenameConversation?: (id: string, title: string) => Promise<void>;
  // Methods props (optional)
  methods?: any[];
  onMethodClick?: (method: any) => void;
}

export function AppLayout({
  children,
  activeView = 'chat',
  onViewChange,
  profile,
  conversations,
  currentConversationId,
  onSelectConversation,
  onNewConversation,
  onDeleteConversation,
  onRenameConversation,
  methods,
  onMethodClick,
}: AppLayoutProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <AppSidebar
          activeView={activeView}
          onViewChange={onViewChange || (() => {})}
          conversations={conversations}
          currentConversationId={currentConversationId}
          onSelectConversation={onSelectConversation}
          onNewConversation={onNewConversation}
          onDeleteConversation={onDeleteConversation}
          onRenameConversation={onRenameConversation}
          methods={methods}
          onMethodClick={onMethodClick}
        />

        <div className="flex-1 flex flex-col h-screen overflow-hidden">
          <header className="h-16 border-b bg-gradient-to-r from-primary to-primary/80 flex items-center px-4 gap-4 sticky top-0 z-50">
            <div className="flex items-center gap-2 ml-auto text-primary-foreground">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate("/profile")}
                      className="flex items-center gap-2 max-w-[200px] text-primary-foreground hover:bg-primary-foreground/10"
                    >
                      <Avatar className="w-8 h-8 flex-shrink-0">
                        <AvatarImage src={profile?.avatar_url} />
                        <AvatarFallback>
                          {profile?.name?.charAt(0).toUpperCase() || <UserIcon className="w-4 h-4" />}
                        </AvatarFallback>
                      </Avatar>
                      <span className="truncate">{profile?.name || t('nav.profile')}</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs">
                    <p>{profile?.name || "Profil"}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </header>

          {children}
        </div>
      </div>
    </SidebarProvider>
  );
}
